import { Contract } from '@ethersproject/contracts'
import { getAddress } from '@ethersproject/address'
import { AddressZero } from '@ethersproject/constants'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import { BigNumber } from '@ethersproject/bignumber'
import { abi as IUniswapV2Router02ABI } from '@uniswap/v2-periphery/build/IUniswapV2Router02.json'
import { getRouterAddress } from '../constants'
import { ChainId, JSBI, Percent, Token, CurrencyAmount, Currency, ETHER } from '@uniswap/sdk'
import { TokenAddressMap } from '../state/lists/hooks'

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

export function getEtherscanLink(chainId: ChainId, data: string, type: 'transaction' | 'token' | 'address'): string {
  // 根据chainId获取对应的区块链浏览器URL
  let prefix: string

  switch (chainId) {
    case ChainId.K:
      prefix = process.env.REACT_APP_K_EXPLORER_URL || process.env.REACT_APP_EXPLORER_URL || 'https://linksay.io'
      break
    case ChainId.BSC:
      prefix = process.env.REACT_APP_BSC_EXPLORER_URL || 'https://bscscan.com'
      break
    case ChainId.MAINNET:
      prefix = 'https://etherscan.io'
      break
    case ChainId.ROPSTEN:
      prefix = 'https://ropsten.etherscan.io'
      break
    case ChainId.RINKEBY:
      prefix = 'https://rinkeby.etherscan.io'
      break
    case ChainId.GÖRLI:
      prefix = 'https://goerli.etherscan.io'
      break
    case ChainId.KOVAN:
      prefix = 'https://kovan.etherscan.io'
      break
    default:
      prefix = process.env.REACT_APP_EXPLORER_URL || 'https://etherscan.io'
  }

  switch (type) {
    case 'transaction': {
      return `${prefix}/tx/${data}`
    }
    case 'token': {
      return `${prefix}/token/${data}`
    }
    case 'address':
    default: {
      return `${prefix}/address/${data}`
    }
  }
}

// shorten the checksummed version of the input address to have 0x + 4 characters at start and end
export function shortenAddress(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
}

// responsive address shortening - returns different lengths based on screen size
export function shortenAddressResponsive(address: string, windowWidth?: number): string {
  const parsed = isAddress(address)
  if (!parsed) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }

  // 如果传入了窗口宽度参数，使用它；否则尝试获取当前窗口宽度
  const width = windowWidth || (typeof window !== 'undefined' ? window.innerWidth : 0)

  if (width <= 400) {
    // 小于400px时显示前2位和后2位
    return `${parsed.substring(0, 4)}...${parsed.substring(40)}`
  }

  // 默认显示前4位和后4位
  return `${parsed.substring(0, 6)}...${parsed.substring(38)}`
}

// add 10%
export function calculateGasMargin(value: BigNumber): BigNumber {
  return value.mul(BigNumber.from(10000).add(BigNumber.from(1000))).div(BigNumber.from(10000))
}

// converts a basis points value to a sdk percent
export function basisPointsToPercent(num: number): Percent {
  return new Percent(JSBI.BigInt(num), JSBI.BigInt(10000))
}

export function calculateSlippageAmount(value: CurrencyAmount, slippage: number): [JSBI, JSBI] {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }
  return [
    JSBI.divide(JSBI.multiply(value.raw, JSBI.BigInt(10000 - slippage)), JSBI.BigInt(10000)),
    JSBI.divide(JSBI.multiply(value.raw, JSBI.BigInt(10000 + slippage)), JSBI.BigInt(10000))
  ]
}

// account is not optional
export function getSigner(library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

// account is optional
export function getProviderOrSigner(library: Web3Provider, account?: string): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library
}

export function getContract(address: string, ABI: any, library: Web3Provider, account?: string): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }

  return new Contract(address, ABI, getProviderOrSigner(library, account) as any)
}

// account is optional
export function getRouterContract(chainId: number, library: Web3Provider, account?: string): Contract {
  const routerAddress = getRouterAddress(chainId as ChainId)
  return getContract(routerAddress, IUniswapV2Router02ABI, library, account)
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function isTokenOnList(defaultTokens: TokenAddressMap, currency?: Currency): boolean {
  if (currency === ETHER) return true
  return Boolean(currency instanceof Token && defaultTokens[currency.chainId]?.[currency.address])
}

// 智能格式化数量显示
export function formatAmount(amount: CurrencyAmount): string {
  const value = parseFloat(amount.toSignificant(18))

  if (value >= 1000) {
    // 大数：显示完整整数 + 最多2位小数
    return value.toFixed(2).replace(/\.?0+$/, '')
  } else {
    // 小数：最多显示4位小数
    return value.toFixed(4).replace(/\.?0+$/, '')
  }
}
